import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from "../lib/axios";
import { useNavigate } from 'react-router-dom';
import { Button } from "../components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form";
import { Input } from "../components/ui/input";
import { CreditCard, Info, AlertCircle, CheckCircle, Loader2, ExternalLink } from "lucide-react";
import { API_BASE_URL, DIGIO_ENVIRONMENT } from '../config';
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLoader } from "../contexts/LoaderContext";
import { useJourney } from "../contexts/JourneyContext";
import { toast } from "sonner";
import JourneyHeader from "@/components/JourneyHeader";
import {
    getDigioConstructor,
    removeOrphanedDigioOverlays,
    resolveDigioToken,
    useDigioSdk,
    type DigioCallbackResponse,
    type DigioEvent,
    type DigioInstance,
    type DigioOptions,
} from "@/hooks/useDigioSdk";

const upiSchema = z.object({
    vpa: z.string().min(3, "VPA is required").regex(/^[\w.-]+@[\w.-]+$/, "Invalid VPA format"),
});

type UpiFormValues = z.infer<typeof upiSchema>;

type MandateType = "UPI" | "NACH";

/**
 * idle        — nothing running, buttons live
 * starting    — create API in flight / iframe being opened
 * in_progress — Digio iframe overlay is open, user is completing the mandate
 * confirming  — Digio reported completion; waiting for backend confirmation
 * cancelled   — user closed the Digio iframe (error_code === "CANCELLED")
 */
type MandateFlowStatus = "idle" | "starting" | "in_progress" | "confirming" | "cancelled";

/**
 * Digio eNACH events we subscribe to (entity: `nach`).
 *
 * BOTH spellings of the UPI cancel event are listed on purpose. Digio's own
 * event table writes it as `nach_upi_mandate.cancelled_by_user` (underscore
 * where every sibling has a dot) — the same kind of upstream typo as their
 * misspelled `DOCUEMNT_NOT_READY_FOR_THIS_ACTION` error code. We cannot tell
 * from the SDK bundle which form actually goes over the wire (the gateway, not
 * digio.js, emits these names), and the filter is an exact-match test
 * (`event_filter.includes(event.data.event)`), so a single wrong guess would
 * silently swallow every UPI cancellation and leave the user waiting for the
 * ~1s watchdog's generic CANCELLED instead. Subscribing to both costs nothing;
 * the handler already keys off `endsWith("cancelled_by_user")`.
 *
 * Read-only so it cannot be handed to the SDK directly — see `event_filter`
 * in buildDigioOptions, which passes a fresh mutable copy per instance.
 */
const MANDATE_EVENTS: readonly string[] = [
    "nach.upi_mandate.vpa_verified",
    "nach.upi_mandate.authenticated",
    "nach.upi_mandate.rejected",
    "nach.upi_mandate.failed",
    "nach.upi_mandate.already_authenticated",
    "nach_upi_mandate.cancelled_by_user", // verbatim from Digio's table (sic)
    "nach.upi_mandate.cancelled_by_user", // dotted form, in case the table is wrong
    "nach.api_mandate.authenticated",
    "nach.api_mandate.failed",
    "nach.api_mandate.cancelled_by_user",
    "nach.api_mandate.already_authenticated",
] as const;

// Safety-poll cadence — unchanged from the previous popup implementation (4s).
const POLL_INTERVAL_MS = 4000;
// Bounded window while the user is still inside the Digio iframe. The old code
// polled for as long as the popup stayed open; an iframe has no `closed` flag,
// so this caps the poll instead.
const IN_PROGRESS_POLL_WINDOW_MS = 5 * 60 * 1000;
// Bounded window after Digio reports completion. Matches the previous
// POST_CLOSE_POLL_SECONDS (90s) — NACH bank confirmation is asynchronous.
const CONFIRM_POLL_WINDOW_MS = 90 * 1000;

/** Narrows an unknown thrown value to our backend's `{ error }` envelope. */
const extractApiError = (err: unknown): string | null => {
    const data = (err as { response?: { data?: { error?: unknown } } })?.response?.data;
    return typeof data?.error === "string" ? data.error : null;
};

const statusEndpoint = (type: MandateType) =>
    type === "UPI"
        ? `${API_BASE_URL}/api/check_upi_mandate_status/`
        : `${API_BASE_URL}/api/check_nach_mandate_status/`;

const createEndpoint = (type: MandateType) =>
    type === "UPI"
        ? `${API_BASE_URL}/api/create_digio_upi_mandate/`
        : `${API_BASE_URL}/api/create_digio_nach_mandate/`;

// ── Gateway routing guard (digio.js v11 `submit()`) ─────────────────────────
// Verified from the live SDK bundle: `submit(ids, identifier, token_id)` first
// dispatches purely on the SHAPE of the id it is given —
//
//   typeof ids === 'string' && ids.slice(0,3) === 'ENA' && ids.slice(-2) === 'AP'
//       -> enachApiSign()  ->  API_MANDATE_SUFFIX = '/#/enach-mandate-direct'
//   anything else (incl. every array)
//       -> esign()         ->  ESIGN_SUFFIX       = '/#/gateway/login'
//
// ⚠️ BUT `enachApiSign()` then RE-ROUTES when a token is supplied:
//
//   var url = CONSTANTS.URLS.API_MANDATE_SUFFIX;          // '/#/enach-mandate-direct'
//   if (token_id) { url = CONSTANTS.URLS.ESIGN_SUFFIX; }  // '/#/gateway/login'
//
// So for an `ENA…AP` (eNACH API) mandate, passing the gateway access token as
// submit()'s 3rd argument moves the flow to '/#/gateway/login' — the id-shape
// rule alone no longer predicts the gateway. UPI mandate ids are `ENA…UP`, so
// they already route via `esign()` and are unaffected by the re-route.
//
// Digio's own `authentication_url` (our `redirect_url`) still tells us which
// gateway Digio considers correct for this mandate, so we keep treating it as
// the authority — but we must compare it against what `submit()` will ACTUALLY
// do given the token, otherwise a token would trigger a false mismatch and
// silently bypass submit() in favour of redirect_url, discarding the benefit.
const ENACH_DIRECT_SUFFIX = "/#/enach-mandate-direct";
const ESIGN_SUFFIX = "/#/gateway/login";

type DigioGateway = "enach-direct" | "esign";

/**
 * Which gateway `digio.submit(id, identifier, tokenId)` would open.
 *
 * `withToken` is what makes this token-aware: when a token WILL be passed, an
 * `ENA…AP` id goes to the eSign gateway instead of the eNACH-direct one (see the
 * `if (token_id)` re-route in `enachApiSign()` quoted above).
 */
const gatewayFromSubmit = (mandateId: string, withToken: boolean): DigioGateway => {
    const isEnachApiId = mandateId.slice(0, 3) === "ENA" && mandateId.slice(-2) === "AP";
    if (!isEnachApiId) return "esign";              // esign() -> ESIGN_SUFFIX
    return withToken ? "esign" : "enach-direct";    // enachApiSign()'s token re-route
};

/**
 * Which gateway Digio itself points at, read from `authentication_url`.
 * Returns null when the URL is absent/unparseable or matches neither suffix,
 * in which case we must not draw any conclusion from it.
 *
 * The gateway path lives in the URL *fragment* (`https://host/#/gateway/login/...`),
 * so we match on the raw string rather than `URL.pathname`.
 */
const gatewayFromRedirectUrl = (url: string | null | undefined): DigioGateway | null => {
    if (!url) return null;
    if (url.includes(ENACH_DIRECT_SUFFIX)) return "enach-direct";
    if (url.includes(ESIGN_SUFFIX)) return "esign";
    return null;
};

const SetupMandates = () => {
    const navigate = useNavigate();
    const { state: journey, refresh: refreshJourney } = useJourney();
    const { loading, setLoading, setLoaderMessage } = useLoader();
    const { loaded: sdkLoaded, error: sdkError } = useDigioSdk();

    const [fetching, setFetching] = useState(true);
    const [customerInfo, setCustomerInfo] = useState({
        identifier: "",
        bankingName: ""
    });

    const [flowStatus, setFlowStatus] = useState<MandateFlowStatus>("idle");
    const [activeType, setActiveType] = useState<MandateType | null>(null);
    // Digio's `authentication_url` — retained as the manual escape hatch / fallback.
    const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);

    const digioRef = useRef<DigioInstance | null>(null);
    const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // Guards against the callback, an event and the poll all resolving the flow.
    const settledRef = useRef(false);
    const mountedRef = useRef(true);
    /**
     * Monotonic "attempt" counter, incremented every time we start (or abandon)
     * a flow.
     *
     * Why this is required: `digio.init()` installs its OWN `setInterval(…, 1000)`
     * watchdog which fires `callback({ error_code: 'CANCELLED' })` as soon as it
     * observes `popup == null` — and `digio.cancel()` sets `popup = null` WITHOUT
     * clearing that interval. So every instance we discard (environment re-init,
     * redirect fallback, new-tab escape hatch, retry) emits a phantom CANCELLED
     * about a second later. Since those paths deliberately reset `settledRef` to
     * false, the phantom would otherwise win the race and cancel the *new*
     * attempt. Each set of callbacks captures the id of the attempt that created
     * it and ignores anything that is no longer current.
     */
    const runIdRef = useRef(0);
    /**
     * Synchronous re-entrancy guard. `busy` is derived from `flowStatus`, i.e.
     * from the render closure, so two clicks dispatched before React re-renders
     * would BOTH see `busy === false` and each call `digio.init()`. The second
     * overlay would then overwrite `digioRef`, leaving the first one orphaned on
     * document.body — a full-viewport, undismissable curtain over the page.
     */
    const startingRef = useRef(false);
    /**
     * Digio's gateway access token (`digio_token_id` / `digio_token_valid_till`
     * from the create-mandate response), stored RAW. Passing it as `submit()`'s
     * 3rd argument makes Digio's gateway log the user in by token instead of
     * rendering its own OTP screen.
     *
     * Kept raw and resolved at the point of use because the TTL is only ~10
     * minutes, and the create response arrives after the callbacks/instance have
     * already been built.
     */
    const tokenIdRef = useRef<string | null>(null);
    const tokenValidTillRef = useRef<unknown>(null);

    const busy = flowStatus === "starting" || flowStatus === "in_progress" || flowStatus === "confirming";
    const isUpiDone = journey?.current_stage !== "UPI_MANDATE";

    const upiForm = useForm<UpiFormValues>({
        resolver: zodResolver(upiSchema),
        mode: "onChange",
        defaultValues: {
            vpa: "",
        },
    });

    useEffect(() => {
        axios
            .post(`${API_BASE_URL}/api/get_verified_mandate_details/`)
            .then((res) => {
                if (res.status === 200) {
                    setCustomerInfo({
                        identifier: String(res.data.customer_identifier),
                        bankingName: res.data.cust_banking_name
                    });
                    if (res.data.vpa) {
                        upiForm.setValue("vpa", res.data.vpa);
                    }
                }
            })
            .catch((err) => {
                console.error("Fetch error:", err);
                toast.error("Failed to fetch mandate details.");
            })
            .finally(() => {
                setFetching(false);
            });
    }, [upiForm]);

    // ── Timer / overlay helpers ──────────────────────────────────────────────
    const clearPoll = useCallback(() => {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    }, []);

    /** Closes the Digio iframe overlay if one is open. Never throws. */
    const closeDigio = useCallback(() => {
        const instance = digioRef.current;
        digioRef.current = null;
        if (!instance) return;
        try {
            instance.cancel();
        } catch (e) {
            console.warn("Digio cancel() failed (overlay may already be closed):", e);
        }
    }, []);

    // Unmount cleanup — the overlay is appended to document.body, so without
    // this it would survive a route change.
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            settledRef.current = true;
            startingRef.current = false;
            // Retire every outstanding SDK callback so the watchdog left running
            // by cancel() cannot call setState on an unmounted component.
            runIdRef.current += 1;
            clearPoll();
            closeDigio();
            // Any overlay Digio still owns is removed by closeDigio(); this catches
            // one that was orphaned by a throw inside init() (see the helper).
            removeOrphanedDigioOverlays();
        };
    }, [clearPoll, closeDigio]);

    // ── Terminal handlers ────────────────────────────────────────────────────
    const finishSuccess = useCallback(async (type: MandateType, statusData: { next_page_url?: string } | undefined) => {
        if (settledRef.current) return;
        settledRef.current = true;
        startingRef.current = false;
        clearPoll();
        closeDigio();

        toast.success(`${type} Mandate verified successfully!`);
        setFlowStatus("idle");
        setActiveType(null);
        setFallbackUrl(null);
        setLoaderMessage(null);
        setLoading(false);

        await refreshJourney();

        if (type === "NACH") {
            navigate(statusData?.next_page_url || '/waitingforupdates');
        }
        // UPI: stay on the page — refreshJourney() advances the journey stage and
        // the NACH box unlocks.
    }, [clearPoll, closeDigio, navigate, refreshJourney, setLoaderMessage, setLoading]);

    const finishFailure = useCallback((type: MandateType, message: string, cancelled = false) => {
        if (settledRef.current) return;
        settledRef.current = true;
        startingRef.current = false;
        clearPoll();
        closeDigio();

        setFlowStatus(cancelled ? "cancelled" : "idle");
        if (!cancelled) {
            setActiveType(null);
        }
        setLoaderMessage(null);
        setLoading(false);

        if (cancelled) {
            toast.info(message);
        } else {
            toast.error(message);
        }
    }, [clearPoll, closeDigio, setLoaderMessage, setLoading]);

    /**
     * Bounded status poll. Resolves the flow only when the backend confirms
     * `is_success === true` — the client-side Digio callback is never trusted on
     * its own for mandate state.
     */
    const startStatusPoll = useCallback((
        type: MandateType,
        windowMs: number,
        onTimeout: () => void,
    ) => {
        clearPoll();
        const deadline = Date.now() + windowMs;

        pollTimerRef.current = setInterval(async () => {
            if (settledRef.current || !mountedRef.current) {
                clearPoll();
                return;
            }
            if (Date.now() > deadline) {
                clearPoll();
                onTimeout();
                return;
            }
            try {
                const res = await axios.post(statusEndpoint(type));
                if (res.data?.is_success === true) {
                    await finishSuccess(type, res.data);
                }
            } catch (_) {
                // Ignore transient polling errors — keep retrying until the deadline.
            }
        }, POLL_INTERVAL_MS);
    }, [clearPoll, finishSuccess]);

    /**
     * Digio reported completion — confirm with our own backend before advancing.
     */
    const confirmWithBackend = useCallback(async (type: MandateType) => {
        if (settledRef.current) return;
        clearPoll();
        setFlowStatus("confirming");
        setLoading(true);
        setLoaderMessage("mandate");

        try {
            const res = await axios.post(statusEndpoint(type));
            if (res.data?.is_success === true) {
                await finishSuccess(type, res.data);
                return;
            }
        } catch (e) {
            console.warn(`Initial ${type} mandate status check failed, falling back to polling:`, e);
        }

        if (type === "NACH") {
            toast.info("Waiting for bank confirmation… please hold on.");
        }

        // Not confirmed yet (NACH bank confirmation is async) — bounded poll.
        startStatusPoll(type, CONFIRM_POLL_WINDOW_MS, () => {
            finishFailure(type, `${type} Mandate could not be confirmed in time. Please try again.`);
        });
    }, [clearPoll, finishFailure, finishSuccess, setLoaderMessage, setLoading, startStatusPoll]);

    // ── Fallback: previous redirect_url behaviour ────────────────────────────
    const openRedirectFallback = useCallback((url: string, type: MandateType, reason: string) => {
        console.warn(`[Digio mandate] Falling back to redirect_url — ${reason}`);
        clearPoll();
        closeDigio();
        // Retire the inline instance's callbacks: cancel() nulls its popup but
        // leaves its 1s watchdog running, which would otherwise fire a phantom
        // CANCELLED and immediately kill this fallback.
        runIdRef.current += 1;
        settledRef.current = false;
        startingRef.current = false;

        setFallbackUrl(url);
        setActiveType(type);
        setFlowStatus("in_progress");
        setLoaderMessage(null);
        setLoading(false);

        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        const popup = window.open(
            url,
            "MandateWindow",
            `width=${width},height=${height},left=${left},top=${top},scrollbars=1`
        );

        if (!popup || popup.closed || typeof popup.closed === "undefined") {
            // Mobile / popup-blocked: full-page redirect, exactly as before.
            console.warn("[Digio mandate] Popup unavailable — using full-page redirect.");
            window.location.href = url;
            return;
        }

        toast.info(`Complete the ${type} mandate in the Digio window that just opened.`);
        startStatusPoll(type, IN_PROGRESS_POLL_WINDOW_MS, () => {
            finishFailure(type, `${type} Mandate was not completed in time. Please try again.`);
        });
    }, [clearPoll, closeDigio, finishFailure, setLoaderMessage, setLoading, startStatusPoll]);

    // ── Digio constructor options ────────────────────────────────────────────
    /**
     * `runId` must be the value of `runIdRef.current` at the moment the instance
     * is constructed; every callback below is a no-op once it is stale.
     */
    const buildDigioOptions = useCallback((
        type: MandateType,
        environment: DigioOptions["environment"],
        runId: number,
    ): DigioOptions => ({
        environment,
        logo: `${window.location.origin}/logo.svg`,
        theme: {
            primaryColor: "#065A4E",
            secondaryColor: "#000000",
        },
        // The only inline mechanism the v11 SDK offers: Digio builds its own
        // full-viewport iframe overlay on document.body instead of a popup.
        is_iframe: true,
        callback: (response: DigioCallbackResponse) => {
            console.log("[Digio mandate] callback:", response);
            if (runId !== runIdRef.current) {
                // Phantom CANCELLED from an instance we already discarded.
                console.warn("[Digio mandate] Ignoring callback from a stale Digio instance:", response);
                return;
            }
            // v11: failure is signalled by the PRESENCE of error_code.
            if (response?.error_code) {
                if (response.error_code === "CANCELLED") {
                    finishFailure(type, `${type} mandate setup was cancelled. You can try again.`, true);
                } else {
                    finishFailure(
                        type,
                        response.message || `${type} Mandate failed (${response.error_code}). Please try again.`
                    );
                }
                return;
            }
            // Success per Digio — still confirm with the backend before advancing.
            void confirmWithBackend(type);
        },
        event_listener: (event: DigioEvent) => {
            console.log("[Digio mandate] event:", event?.event, event?.payload?.type);
            if (runId !== runIdRef.current) return;
            const name = event?.event ?? "";

            if (name.endsWith("authenticated")) {
                // covers *.authenticated and *.already_authenticated
                void confirmWithBackend(type);
                return;
            }
            if (name.endsWith("cancelled_by_user")) {
                finishFailure(type, `${type} mandate setup was cancelled. You can try again.`, true);
                return;
            }
            if (name.endsWith("rejected") || name.endsWith("failed")) {
                const detail = event?.payload?.error?.message;
                finishFailure(type, detail || `${type} Mandate was declined. Please try again.`);
            }
        },
        // digio.js v11 validates this with
        // `t.event_filter.constructor.name !== 'Array'` and then uses it as
        // `this.event_filter.includes(event.data.event)` — so it must be a
        // PLAIN ARRAY. Passing `{ events: [...] }` made the constructor throw
        // (DigioException), which the catch below downgraded to the redirect
        // fallback, silently killing the inline iframe on every attempt.
        //
        // A FRESH COPY per instance: the SDK keeps the very array we pass
        // (`this.event_filter = t.event_filter`, no clone) and its sibling
        // `esign()` mutates caller-owned arrays in place (`ids.shift()`), so a
        // shared module-level array must never be handed over. Copying keeps
        // MANDATE_EVENTS authoritative for every later attempt.
        event_filter: [...MANDATE_EVENTS],
    }), [confirmWithBackend, finishFailure]);

    /**
     * Launches a mandate inline.
     *
     * Ordering matters: `new Digio(...)` + `digio.init()` run synchronously
     * inside the user-gesture handler, BEFORE the create-mandate `await`, which
     * is the same anti-blocker ordering used by the DigiCheck journey.
     */
    const startMandate = useCallback(async (type: MandateType, vpa: string) => {
        if (busy || startingRef.current) return;
        startingRef.current = true;

        settledRef.current = false;
        // New attempt — invalidates the callbacks of every previous instance.
        const runId = ++runIdRef.current;
        clearPoll();
        setFallbackUrl(null);
        setActiveType(type);
        setFlowStatus("starting");
        setLoading(true);
        setLoaderMessage("mandate");

        const DigioCtor = getDigioConstructor();
        let digio: DigioInstance | null = null;
        let inlineReady = false;

        if (!sdkLoaded || !DigioCtor) {
            console.warn(
                `[Digio mandate] Inline iframe unavailable (sdkLoaded=${sdkLoaded}, window.Digio=${Boolean(DigioCtor)}) — will use redirect_url.`
            );
        } else {
            try {
                // `environment` is refined below with the value returned by the
                // backend; DIGIO_ENVIRONMENT is the synchronous default.
                digio = new DigioCtor(buildDigioOptions(type, DIGIO_ENVIRONMENT, runId));
                digio.init();
                digioRef.current = digio;
                inlineReady = true;
            } catch (initError) {
                console.warn("[Digio mandate] init() threw — will use redirect_url.", initError);
                digio = null;
                digioRef.current = null;
                // init() appends its overlay to document.body BEFORE it sets
                // this.popup, so a throw in between leaves a full-viewport
                // curtain that cancel() cannot remove (it needs this.popup).
                removeOrphanedDigioOverlays();
            }
        }

        let data: {
            mandate_id?: string | null;
            customer_identifier?: string | null;
            digio_environment?: 'sandbox' | 'production' | null;
            redirect_url?: string | null;
            // Additive fields; absent/null on tenants or flows without a token.
            digio_token_id?: string | null;
            digio_token_valid_till?: unknown;
        };

        try {
            const response = await axios.post(createEndpoint(type), {
                customer_identifier: customerInfo.identifier,
                cust_banking_name: customerInfo.bankingName,
                vpa,
            });

            if (response.status !== 200 || !response.data) {
                throw new Error("Mandate setup could not be initiated.");
            }
            data = response.data;
        } catch (err: unknown) {
            closeDigio();
            clearPoll();
            settledRef.current = true;
            startingRef.current = false;
            runIdRef.current += 1;
            setFlowStatus("idle");
            setActiveType(null);
            setLoaderMessage(null);
            setLoading(false);
            toast.error(
                extractApiError(err) || `An error occurred initiating ${type} mandate.`
            );
            return;
        }

        const mandateId = data.mandate_id;
        const identifier = data.customer_identifier || customerInfo.identifier;
        const redirectUrl = data.redirect_url || null;
        setFallbackUrl(redirectUrl);
        tokenIdRef.current = data.digio_token_id ?? null;
        tokenValidTillRef.current = data.digio_token_valid_till ?? null;

        // Environment reported by the backend wins; DIGIO_ENVIRONMENT is the fallback.
        const environment = data.digio_environment || DIGIO_ENVIRONMENT;

        // ── Fallback conditions ───────────────────────────────────────────────
        if (!inlineReady || !digio) {
            if (redirectUrl) {
                openRedirectFallback(redirectUrl, type, "Digio SDK/iframe could not be initialised");
            } else {
                finishFailure(type, `Unable to start the ${type} mandate. Please try again.`);
            }
            return;
        }
        if (!mandateId) {
            if (redirectUrl) {
                openRedirectFallback(redirectUrl, type, "mandate_id missing from the create response");
            } else {
                finishFailure(type, `Unable to start the ${type} mandate. Please try again.`);
            }
            return;
        }

        // ── Gateway-routing guard (token-aware) ───────────────────────────────
        // `submit()` picks the gateway from the id shape AND from whether a token
        // is passed (`enachApiSign()` re-routes `ENA…AP` from
        // '/#/enach-mandate-direct' to '/#/gateway/login' when `token_id` is
        // truthy). Digio's own `authentication_url` states which gateway Digio
        // considers correct for this mandate. If the two disagree, `submit()`
        // would silently open the WRONG gateway and the user would see a broken
        // or unrelated screen with no error anywhere. Digio's URL wins.
        //
        // The token must therefore be resolved BEFORE the guard runs, and the
        // prediction must use the token-aware rule — otherwise every NACH
        // `ENA…AP` mandate with a token would look like a mismatch and bypass
        // submit() entirely.
        // Read back through the refs (assigned just above) so this flow uses the
        // same ref-based plumbing as the eSign / KYC flows.
        let tokenId = resolveDigioToken(
            tokenIdRef.current,
            tokenValidTillRef.current,
            `${type} mandate`,
        );

        const expectedGateway = gatewayFromRedirectUrl(redirectUrl);
        let submitGateway = gatewayFromSubmit(mandateId, Boolean(tokenId));
        if (expectedGateway && expectedGateway !== submitGateway) {
            // Minimal, clearly-correct resolution order:
            //   1. If a token is what moved the routing (i.e. the documented
            //      token-less routing DOES agree with Digio's URL), drop the token
            //      and keep the inline flow. The token is only an optimisation, so
            //      giving it up is strictly better than abandoning submit() and
            //      losing the in-page iframe as well.
            //   2. Otherwise this is a genuine, token-independent mismatch —
            //      behave exactly as before and drive Digio's own URL.
            const tokenlessGateway = gatewayFromSubmit(mandateId, false);
            if (tokenId && tokenlessGateway === expectedGateway) {
                console.error(
                    "[Digio mandate] GATEWAY ROUTING MISMATCH caused by the access token — retrying WITHOUT it. " +
                    `type=${type} mandate_id=${mandateId} ` +
                    `submit() with a token would open the "${submitGateway}" gateway ` +
                    `(enachApiSign() re-routes ENA…AP to ${ESIGN_SUFFIX} when token_id is set), ` +
                    `but Digio's authentication_url points at the "${expectedGateway}" gateway ` +
                    `(authentication_url=${redirectUrl}). ` +
                    "Dropping the token restores the documented 2-argument routing; the gateway will ask for its own OTP."
                );
                tokenId = undefined;
                submitGateway = tokenlessGateway;
            } else {
                console.error(
                    "[Digio mandate] GATEWAY ROUTING MISMATCH — refusing to trust submit(). " +
                    `type=${type} mandate_id=${mandateId} token_used=${Boolean(tokenId)} ` +
                    `submit() would open the "${submitGateway}" gateway ` +
                    `(id-shape rule: starts with "ENA" && ends with "AP" => enach-direct, ` +
                    `unless a token re-routes it to ${ESIGN_SUFFIX}), ` +
                    `but Digio's authentication_url points at the "${expectedGateway}" gateway. ` +
                    `authentication_url=${redirectUrl}. ` +
                    "Taking the safe path: opening Digio's own authentication_url instead."
                );
                // Safe path: drive Digio's own URL rather than an SDK-derived one.
                openRedirectFallback(
                    redirectUrl as string,
                    type,
                    `gateway mismatch (submit()=${submitGateway}, authentication_url=${expectedGateway})`
                );
                return;
            }
        }
        if (!expectedGateway) {
            // Not fatal — we just could not corroborate the routing. Log it so a
            // future Digio URL-scheme change is visible in UAT.
            console.warn(
                `[Digio mandate] Could not determine the gateway from authentication_url (${redirectUrl}); ` +
                `proceeding with submit() which will open the "${submitGateway}" gateway ` +
                `(token_used=${Boolean(tokenId)}).`
            );
        }

        // If the backend disagrees with the build-time environment, rebuild the
        // instance so `submit()` targets the right Digio gateway.
        if (environment !== DIGIO_ENVIRONMENT && DigioCtor) {
            try {
                closeDigio();
                digio = new DigioCtor(buildDigioOptions(type, environment, runId));
                digio.init();
                digioRef.current = digio;
            } catch (reinitError) {
                console.warn("[Digio mandate] Re-init for backend environment threw:", reinitError);
                // Same as above: a half-built overlay may be attached.
                digioRef.current = null;
                removeOrphanedDigioOverlays();
                if (redirectUrl) {
                    openRedirectFallback(redirectUrl, type, "re-init for the backend-reported environment failed");
                } else {
                    finishFailure(type, `Unable to start the ${type} mandate. Please try again.`);
                }
                return;
            }
        }

        // ── Open the flow inline ──────────────────────────────────────────────
        // NOTE: digio.js swallows validation errors inside submit() (it wraps the
        // body in try/catch and only `console.error`s), so a failure here usually
        // manifests as "iframe stays on the loader" rather than a throw. The
        // bounded in-progress poll below is what rescues that case.
        //
        // 3rd argument = Digio's gateway access token, so `AuthComponent`'s token
        // login runs instead of its OTP screen. `undefined` (missing / expired /
        // malformed token, or the mismatch retry above) reproduces the previous
        // 2-argument call exactly. A token the gateway rejects is safe: it strips
        // `token_id` and re-renders the OTP screen, because we always pass a real
        // identifier.
        try {
            digio.submit(mandateId, identifier, tokenId);
        } catch (submitError) {
            if (redirectUrl) {
                openRedirectFallback(redirectUrl, type, `submit() threw: ${String(submitError)}`);
            } else {
                finishFailure(type, `Unable to open the ${type} mandate form. Please try again.`);
            }
            return;
        }

        // The Digio overlay now covers the viewport — hide our own full-screen
        // loader so the user can actually see and use it.
        // `flowStatus` now makes `busy` true, so the synchronous guard can go.
        startingRef.current = false;
        setFlowStatus("in_progress");
        setLoaderMessage(null);
        setLoading(false);

        // Safety net in case neither the callback nor an event fires.
        startStatusPoll(type, IN_PROGRESS_POLL_WINDOW_MS, () => {
            finishFailure(type, `${type} Mandate was not completed in time. Please try again.`);
        });
    }, [
        busy,
        buildDigioOptions,
        clearPoll,
        closeDigio,
        customerInfo.bankingName,
        customerInfo.identifier,
        finishFailure,
        openRedirectFallback,
        sdkLoaded,
        setLoaderMessage,
        setLoading,
        startStatusPoll,
    ]);

    const handleUpiSubmit = (values: UpiFormValues) => {
        void startMandate("UPI", values.vpa);
    };

    const handleNachSubmit = () => {
        // NACH uses the bank details already on file — no VPA.
        void startMandate("NACH", "");
    };

    /** Manual escape hatch — open the Digio page in a new tab. */
    const openInNewTab = () => {
        if (!fallbackUrl || !activeType) return;
        console.warn("[Digio mandate] User requested the manual new-tab escape hatch.");
        clearPoll();
        closeDigio();
        // A stale overlay from a throw inside init() would otherwise stay on top
        // of the page the user is being sent back to.
        removeOrphanedDigioOverlays();
        window.open(fallbackUrl, "_blank", "noopener,noreferrer");
        // See openRedirectFallback: retire the closed instance's watchdog.
        runIdRef.current += 1;
        settledRef.current = false;
        // MUST be released: if this hatch is used while startMandate() is still
        // between its create-API await and submit(), startingRef stays true and
        // every later Setup/Retry click returns at the very first guard, leaving
        // the buttons permanently inert.
        startingRef.current = false;
        setFlowStatus("in_progress");
        startStatusPoll(activeType, IN_PROGRESS_POLL_WINDOW_MS, () => {
            finishFailure(activeType, `${activeType} Mandate was not completed in time. Please try again.`);
        });
    };

    const resetFlow = () => {
        clearPoll();
        closeDigio();
        removeOrphanedDigioOverlays();
        // See openRedirectFallback: retire the closed instance's watchdog.
        runIdRef.current += 1;
        settledRef.current = false;
        // See openInNewTab: without this a reset from a half-started attempt
        // would leave the triggers permanently disabled.
        startingRef.current = false;
        setLoaderMessage(null);
        setLoading(false);
        setFlowStatus("idle");
        setActiveType(null);
        setFallbackUrl(null);
    };

    if (fetching) return null;

    // While the SDK is still loading we keep the triggers disabled; once loading
    // has definitively FAILED (sdkError) we re-enable them so the user can still
    // complete the mandate through the redirect_url fallback.
    const sdkPending = !sdkLoaded && !sdkError;
    const upiDisabled = loading || busy || sdkPending || !upiForm.formState.isValid || isUpiDone;
    const nachDisabled = loading || busy || sdkPending || !isUpiDone;

    const buttonLabel = (base: string, type: MandateType) => {
        if (sdkPending) return "Loading secure form...";
        if (activeType === type) {
            if (flowStatus === "starting") return "Opening secure form...";
            if (flowStatus === "in_progress") return "Complete the mandate above...";
            if (flowStatus === "confirming") return "Confirming...";
        }
        if (busy) return "Processing...";
        return base;
    };

    return (
        <div className="min-h-screen bg-intellend-white">
            <JourneyHeader />

            <main className="container mx-auto px-4 py-8 max-w-2xl">
                <div className="bg-white shadow-lg rounded-xl p-8 border border-gray-100">
                    <div className="flex flex-col items-center mb-8">
                        <div className="bg-intellend-green/10 p-4 rounded-full mb-4">
                            <CreditCard className="text-intellend-green h-8 w-8" />
                        </div>
                        <h1 className="text-2xl font-bold text-center text-intellend-green">Setup Repayment Mandates</h1>
                        <p className="text-gray-500 text-sm mt-2 text-center">
                            Please complete both UPI and NACH mandate registrations to proceed.
                            Each step opens securely on this page — you will not be taken away from your application.
                        </p>
                    </div>

                    <div className="space-y-6 mb-8">
                        <div>
                            <p className="text-sm font-semibold text-gray-700">Customer Identifier (Phone)</p>
                            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mt-1 text-gray-600">
                                {customerInfo.identifier || "-"}
                            </div>
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-gray-700">Customer Banking Name</p>
                            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mt-1 text-gray-600">
                                {customerInfo.bankingName || "-"}
                            </div>
                        </div>
                    </div>

                    {/* SDK loading / load-failure banners */}
                    {!sdkLoaded && !sdkError && (
                        <div className="mb-6 bg-blue-50 border border-blue-200 p-3 rounded-lg flex items-center gap-3 text-sm text-blue-700">
                            <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                            <p>Loading the secure mandate form…</p>
                        </div>
                    )}
                    {sdkError && (
                        <div className="mb-6 bg-amber-50 border border-amber-200 p-3 rounded-lg flex gap-3 text-sm text-amber-800">
                            <AlertCircle className="h-5 w-5 shrink-0" />
                            <p>
                                The in-page mandate form could not be loaded. You can still continue —
                                the mandate will open in a separate secure window instead.
                            </p>
                        </div>
                    )}

                    {/* In-progress / confirming banner (the Digio overlay covers the viewport) */}
                    {activeType && (flowStatus === "in_progress" || flowStatus === "confirming") && (
                        <div className="mb-6 bg-blue-50 border border-blue-200 p-4 rounded-lg space-y-3 text-sm text-blue-800">
                            <div className="flex gap-3">
                                <Loader2 className="h-5 w-5 animate-spin shrink-0" />
                                <p>
                                    {flowStatus === "confirming"
                                        ? `Confirming your ${activeType} mandate with the bank. Please do not close this page.`
                                        : `Complete the ${activeType} mandate in the secure Digio form. This page will update automatically once you finish.`}
                                </p>
                            </div>
                            {fallbackUrl && flowStatus === "in_progress" && (
                                <button
                                    type="button"
                                    onClick={openInNewTab}
                                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-intellend-green underline underline-offset-2"
                                >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                    Having trouble? Open the mandate page in a new tab
                                </button>
                            )}
                            {/* User-driven exit for BOTH in-progress states. Without it
                                the only way out of `confirming` was to wait out the 90s
                                deadline, and out of `in_progress` (with no fallbackUrl)
                                the full 5 minutes — with Digio's own overlay on top and
                                our buttons disabled by `busy`. Parity with the eSign /
                                KYC "Cancel and start over" hatch. */}
                            <button
                                type="button"
                                onClick={resetFlow}
                                className="block text-xs text-gray-500 underline underline-offset-2"
                            >
                                Cancel and start over
                            </button>
                        </div>
                    )}

                    {/* User-cancelled state with a retry affordance */}
                    {flowStatus === "cancelled" && activeType && (
                        <div className="mb-6 bg-amber-50 border border-amber-200 p-4 rounded-lg space-y-3 text-sm text-amber-800">
                            <div className="flex gap-3">
                                <AlertCircle className="h-5 w-5 shrink-0" />
                                <p>
                                    You closed the {activeType} mandate form before it was completed.
                                    Your mandate has not been registered yet.
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                <Button
                                    type="button"
                                    onClick={() => {
                                        const type = activeType;
                                        resetFlow();
                                        void startMandate(type, type === "UPI" ? upiForm.getValues("vpa") : "");
                                    }}
                                    className="bg-intellend-green hover:bg-intellend-green/90 h-9 text-sm font-semibold"
                                >
                                    Retry {activeType} Mandate
                                </Button>
                                {fallbackUrl && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={openInNewTab}
                                        className="h-9 text-sm"
                                    >
                                        <ExternalLink className="h-4 w-4 mr-1.5" />
                                        Open in new tab
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="space-y-6 relative">
                        {/* 1. UPI Mandate Box */}
                        <div className={`border rounded-xl p-6 transition-all duration-500 relative ${isUpiDone ? "border-green-200 bg-green-50 overflow-hidden" : "border-intellend-green/30 bg-white shadow-sm"}`}>
                            {isUpiDone && (
                                <div className="absolute inset-0 z-10 bg-white/60 backdrop-blur-[2px] rounded-xl flex items-center justify-center">
                                    <div className="flex items-center gap-2 text-green-700 font-bold text-lg bg-white px-4 py-2 rounded-full shadow-sm border border-green-100">
                                        <CheckCircle className="h-6 w-6 text-green-600" />
                                        UPI Mandate Registered
                                    </div>
                                </div>
                            )}

                            <h2 className="text-xl font-bold text-gray-800 mb-4">1. UPI Autopay Mandate</h2>
                            <Form {...upiForm}>
                                <form onSubmit={upiForm.handleSubmit(handleUpiSubmit)} className="space-y-4">
                                    <FormField
                                        control={upiForm.control}
                                        name="vpa"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="text-gray-700 font-semibold flex items-center gap-2">
                                                    UPI VPA (Virtual Private Address) <span className="text-red-500">*</span>
                                                </FormLabel>
                                                <FormControl>
                                                    <Input placeholder="e.g. mobile@upi" {...field} className="border-intellend-green/30 focus-visible:ring-intellend-green h-12 text-lg" disabled={isUpiDone || busy} />
                                                </FormControl>
                                                <div className="flex items-start gap-2 text-xs text-blue-600 mt-1">
                                                    <Info size={14} className="mt-0.5" />
                                                    <span>Example: 9876543210@upi, john.doe@okaxis, etc.</span>
                                                </div>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg flex gap-3 text-sm text-amber-800">
                                        <AlertCircle className="h-5 w-5 shrink-0" />
                                        <p>By clicking "Setup Mandate", you agree to authorize automatic repayments via UPI.</p>
                                    </div>
                                    <Button
                                        type="submit"
                                        className="w-full bg-intellend-green hover:bg-intellend-green/90 h-12 text-lg font-bold shadow-md transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                                        disabled={upiDisabled}
                                        aria-disabled={upiDisabled}
                                    >
                                        {sdkPending || (activeType === "UPI" && busy) ? (
                                            <>
                                                {buttonLabel("Setup UPI Mandate", "UPI")}
                                                <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                                            </>
                                        ) : (
                                            buttonLabel("Setup UPI Mandate", "UPI")
                                        )}
                                    </Button>
                                </form>
                            </Form>
                        </div>

                        {/* 2. NACH Mandate Box */}
                        <div className={`border rounded-xl p-6 transition-all duration-500 ${!isUpiDone ? "border-gray-200 bg-gray-50 opacity-60 pointer-events-none" : "border-intellend-green/30 bg-white shadow-sm"}`}>
                            <h2 className="text-xl font-bold text-gray-800 mb-4">2. NACH Mandate</h2>
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600">
                                    Your bank account details will be automatically fetched for the NACH registration based on the information provided earlier.
                                </p>
                                <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg flex gap-3 text-sm text-amber-800">
                                    <AlertCircle className="h-5 w-5 shrink-0" />
                                    <p>By clicking "Setup Mandate", you agree to authorize automatic monthly repayments via NACH.</p>
                                </div>
                                <Button
                                    type="button"
                                    onClick={handleNachSubmit}
                                    className="w-full bg-intellend-green hover:bg-intellend-green/90 h-12 text-lg font-bold shadow-md transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                                    disabled={nachDisabled}
                                    aria-disabled={nachDisabled}
                                >
                                    {sdkPending || (activeType === "NACH" && busy) ? (
                                        <>
                                            {buttonLabel("Setup NACH Mandate", "NACH")}
                                            <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                                        </>
                                    ) : (
                                        buttonLabel("Setup NACH Mandate", "NACH")
                                    )}
                                </Button>
                            </div>
                        </div>

                        <p className="text-center text-xs text-gray-400 mt-6 italic">Securely powered by Digio</p>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default SetupMandates;
